import { useIsMobile } from '@brunommsilva/react-foundations';
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Toolbar,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import appConfig from '../../config/app-config';
import * as PATH from '../../config/paths';
import useLayout from '../../hooks/layout/use-layout';
import {
  Account,
  Accounting,
  Assistant,
  Category,
  Chart,
  Transaction,
} from '../icons';
import TopBar from './top-bar';
import UserMenu from './user-menu';

const NavigationBar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const basePath = appConfig.getBasePath();

  const isMobile = useIsMobile();
  const { toolbarHeight, barZIndex } = useLayout();

  const handleTitleClick = () => {
    navigate(basePath || PATH.DASHBOARD_PATH);
  };

  const navItems = [
    { path: PATH.DASHBOARD_PATH, label: 'DASHBOARD', Icon: Chart },
    { path: PATH.ACCOUNTS_PATH, label: 'ACCOUNTS', Icon: Account },
    { path: PATH.TRANSACTIONS_PATH, label: 'TRANSACTIONS', Icon: Transaction },
    { path: PATH.CATEGORIES_PATH, label: 'CATEGORIES', Icon: Category },
  ];

  // On a phone the assistant is a page of its own, last in the bottom navigation.
  const mobileNavItems = appConfig.isAssistantEnabled()
    ? [
        ...navItems,
        {
          path: PATH.ASSISTANT_PATH,
          label: 'ASSISTANT_TITLE',
          Icon: Assistant,
        },
      ]
    : navItems;

  if (isMobile) {
    return (
      <>
        <TopBar />

        {/* Mobile Bottom Navigation */}
        <Paper
          square
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: '1.5px solid',
            borderColor: 'divider',
            pb: 'var(--bottom-nav-inset)',
            bgcolor: 'background.paper',
            zIndex: barZIndex,
          }}
          elevation={4}
        >
          <BottomNavigation
            value={pathname}
            onChange={(_, newValue) => navigate(newValue)}
            showLabels
            sx={{
              height: 'var(--bottom-nav-height)',
              bgcolor: 'transparent',
            }}
          >
            {mobileNavItems.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={t(item.label)}
                icon={<item.Icon />}
                value={item.path}
                sx={{
                  minWidth: 0,
                  px: 0.5,
                  '& .MuiBottomNavigationAction-label, &.Mui-selected .MuiBottomNavigationAction-label':
                    { fontSize: '0.7rem' },
                  '& .MuiSvgIcon-root': { fontSize: '1.35rem' },
                  '&.Mui-selected': { color: 'primary.main' },
                  color: 'text.secondary',
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </>
    );
  }

  // Desktop Navigation
  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        containerType: 'inline-size',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            display: 'grid',
            // The options are centred, whatever the width of the name and of the user menu.
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            minHeight: toolbarHeight,
          }}
        >
          {/* Brand/Logo */}
          <Box
            onClick={handleTitleClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <Accounting color="primary" sx={{ fontSize: 28 }} />
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.5px',
                color: 'text.primary',
                // The name leaves room for the menu when the bar is narrow, as with the assistant docked.
                '@container (max-width: 1000px)': { display: 'none' },
              }}
            >
              {t('APP_NAME')}
            </Typography>
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {navItems.map((item, index) => {
              const isActive = pathname === item.path;
              return (
                <React.Fragment key={item.path}>
                  {index > 0 && (
                    <Divider
                      orientation="vertical"
                      sx={{ mx: 0.5, height: 20 }}
                    />
                  )}
                  <Button
                    variant={isActive ? 'contained' : 'text'}
                    color={isActive ? 'primary' : 'inherit'}
                    onClick={() => navigate(item.path)}
                    startIcon={<item.Icon />}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive
                        ? 'primary.contrastText'
                        : 'text.secondary',
                      '&:hover': {
                        color: isActive
                          ? 'primary.contrastText'
                          : 'text.primary',
                      },
                    }}
                  >
                    {t(item.label)}
                  </Button>
                </React.Fragment>
              );
            })}
          </Box>

          <Box sx={{ justifySelf: 'end' }}>
            <UserMenu />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default NavigationBar;
